export const getUpdatedRichTextNodes = (nodes) => {
  const richTextIds = [];
  if (!Array.isArray(nodes)) return richTextIds;

  nodes.forEach((section) => {
    if (section.steps && Array.isArray(section.steps)) {
      section.steps.forEach((step) => {
        // Check step children
        if (step.children && Array.isArray(step.children)) {
          step.children.forEach((child) => {
            if (
              child.type === "rich-text" &&
              child.detail?.is_updated_master === true &&
              child.detail?.rich_text_node_id
            ) {
              richTextIds.push(child.detail.rich_text_node_id);
            }
          });
        }
        // Check substeps
        if (step.substeps && Array.isArray(step.substeps)) {
          step.substeps.forEach((substep) => {
            if (substep.children && Array.isArray(substep.children)) {
              substep.children.forEach((child) => {
                if (
                  child.type === "rich-text" &&
                  child.detail?.is_updated_master === true &&
                  child.detail?.rich_text_node_id
                ) {
                  richTextIds.push(child.detail.rich_text_node_id);
                }
              });
            }
          });
        }
      });
    }
  });
  return richTextIds;
};

export const getUpdatedSteps = (nodes) => {
  const stepsMap = [];
  if (!Array.isArray(nodes)) return stepsMap;

  nodes.forEach((section) => {
    if (section.steps && Array.isArray(section.steps)) {
      section.steps.forEach((step) => {
        const stepData = step.detail?.step_data;
        if (!stepData) return;

        const stepTreeNodeId = step.detail?.step_tree_node_id;
        if (!stepTreeNodeId) return;

        const updatedStepDetails = [];
        if (
          stepData.step_details &&
          Array.isArray(stepData.step_details)
        ) {
          stepData.step_details.forEach((item) => {
            if (item.is_updated_parts && item.part_ref) {
              updatedStepDetails.push(item.part_ref);
            }
          });
        }

        const updatedPartsRequired = [];
        if (
          stepData.parts_required &&
          Array.isArray(stepData.parts_required)
        ) {
          stepData.parts_required.forEach((item) => {
            if (item.is_updated_parts && item.part_ref) {
              updatedPartsRequired.push(item.part_ref);
            }
          });
        }

        const isUpdatedMaster = step.detail?.is_updated_master === true;

        if (
          updatedStepDetails.length > 0 ||
          updatedPartsRequired.length > 0 ||
          isUpdatedMaster
        ) {
          const stepEntry = {
            step_details: updatedStepDetails,
            parts_required: updatedPartsRequired,
          };

          if (isUpdatedMaster) {
            stepEntry.bom_table_for_step_title_ref =
              step.detail.bom_table_for_step_title_ref;
            stepEntry.part_number_column_title_ref =
              step.detail.part_number_column_title_ref;
          }

          stepsMap.push({
            [stepTreeNodeId]:stepEntry
          });
        }
      });
    }
  });
  return stepsMap;
};

export const getUpdatedCanvasNodes = (nodes) => {
  const canvasMap = {};
  if (!Array.isArray(nodes)) return canvasMap;

  const processChildren = (children) => {
    if (!Array.isArray(children)) return;
    children.forEach((child) => {
      if (child.type === "canva" && child.detail?.ctm_data) {
        const updatedParts = [];
        child.detail.ctm_data.forEach((layout) => {
          if (layout.text_boxes && Array.isArray(layout.text_boxes)) {
            layout.text_boxes.forEach((box) => {
              if (
                box.text_box_content &&
                Array.isArray(box.text_box_content)
              ) {
                box.text_box_content.forEach((content) => {
                  if (content.is_updated_parts && content.part_ref) {
                    updatedParts.push(content.part_ref);
                  }
                });
              }
            });
          }
        });

        if (updatedParts.length > 0) {
          const canvasId = child.detail.canvas_tree_node_id;
          if (canvasId) {
            canvasMap[canvasId] = [...new Set(updatedParts)];
          }
        }
      }
    });
  };

  nodes.forEach((section) => {
    if (section.steps && Array.isArray(section.steps)) {
      section.steps.forEach((step) => {
        processChildren(step.children);
        if (step.substeps && Array.isArray(step.substeps)) {
          step.substeps.forEach((substep) => {
            processChildren(substep.children);
          });
        }
      });
    }
  });

  return canvasMap;
};

export const getInfoToUpdate = (revisionNodes) => {
  return {
    steps: getUpdatedSteps(revisionNodes),
    rich_text: getUpdatedRichTextNodes(revisionNodes),
    canvas: getUpdatedCanvasNodes(revisionNodes),
  };
};


/** Locked (released) revisions: not editable BOM from API; use snapshot / nodes. */
export const isLocked = (status) => {
  if (status == null || status === "") return false;
  const s = String(status).toLowerCase();
  return s !== "not submitted" && s !== "change requested";
};

export const bomRowsMapper = (bom_rows) =>{
  return bom_rows.map(d => ({
      ...d,
      part_info: {
        ...d.part_info,
        type_info: {
          name: d.type,
          orders: d.type_orders ?? d.part_info?.type_info?.orders ?? d.part_info?.part_info?.type_info?.orders
        },
        part_number: d.partNumber,
        description: d.description,
        table_note: d.table_note,
        unit_info: { unit_name: d.unit_name }
      }
    }))
}

export const StepDetailsMapper = (step_details) =>{
  return step_details.map(d => ({
      ...d,
      part_info: {
        ...d.part_info,
        part_number: d.partNumber,
        description: d.description
      }
  }))
}

export const parseRevision = (rev) => {
    if (!rev) return null;

    // Check for special characters only
    const hasSpecialChars = /[@#$%!^&*]/.test(rev);
    if (hasSpecialChars) {
      return {
        base: "",
        type: "special_chars",
        minorNum: 0,
        amendChar: "",
        original: rev,
      };
    }

    // Standalone dash
    if (rev === "-") {
      return {
        base: "",
        type: "special",
        minorNum: 0,
        amendChar: "",
        original: rev,
      };
    }

    // e.g., A-, AB-, ZY-
    if (/^[A-Z]{1,3}-$/.test(rev)) {
      return {
        base: rev.slice(0, -1),
        type: "major",
        minorNum: 0,
        amendChar: "",
        original: rev,
      };
    }

    // e.g., A1-a, B2-c
    const amendMatchWithHyphen = rev.match(/^([A-Z]{1,3})(\d+)-([a-z]+)$/);
    if (amendMatchWithHyphen) {
      const [, base, num, amendChar] = amendMatchWithHyphen;
      return {
        base,
        type: "amend",
        minorNum: parseInt(num),
        amendChar,
        original: rev,
      };
    }

    // ✅ NEW: e.g., A-a, AB-b (amendment without minor number)
    const amendMinorlessMatch = rev.match(/^([A-Z]{1,3})-([a-z]+)$/);
    if (amendMinorlessMatch) {
      const [, base, amendChar] = amendMinorlessMatch;
      return {
        base,
        type: "amend_minorless",
        minorNum: 0,
        amendChar,
        original: rev,
      };
    }

    // e.g., A1, A2a
    const minorMatch = rev.match(/^([A-Z]{1,3})(\d+)([a-z]*)$/);
    if (minorMatch) {
      const [, base, num, amendChar] = minorMatch;
      return {
        base,
        type: amendChar ? "amend" : "minor",
        minorNum: parseInt(num),
        amendChar: amendChar || "",
        original: rev,
      };
    }

    return null;
  };

export const revisionSort = (a, b) => {
  const revA = parseRevision(a.rev);
  const revB = parseRevision(b.rev);
  if (!revA && !revB) return 0;
  if (!revA) return 1;
  if (!revB) return -1;
  if (revA.type === "special") return -1;
  if (revB.type === "special") return 1;
  if (revA.base !== revB.base)
    return revA.base.localeCompare(revB.base);
  if (revA.type === "major" && revB.type !== "major") return -1;
  if (revB.type === "major" && revA.type !== "major") return 1;
  if (revA.minorNum !== revB.minorNum)
    return revA.minorNum - revB.minorNum;
  return revA.amendChar.localeCompare(revB.amendChar);
}